/**
 * A-2: `Stream` type + normalizer for the browse module.
 *
 * Wire source: GET /api/broadcasts (base/views.py get_broadcasts →
 * BroadcastSerializer: every Broadcast model field, `_id` pk, nested
 * `channel: {title, avatar_url, handle}`). Everything downstream of the
 * browse module consumes `Stream`, never the raw payload.
 *
 * Ubiquitous language: a stream is `live` or `upcoming` (from `is_live`;
 * the wire `status` string — "", "late", "complete" — never leaks through).
 * `viewerCount` is watchers, concurrent. The source's own timezone label
 * (`timing_string`) is discarded and never read.
 */

/**
 * @typedef {Object} Stream
 * @property {number} id
 * @property {string} title
 * @property {?string} channelName
 * @property {?string} channelAvatarUrl
 * @property {?string} thumbnailUrl
 * @property {?string} parkCode  e.g. "WDW", "DLR" — null when unknown
 * @property {'live'|'upcoming'} status
 * @property {?number} viewerCount  concurrent watchers; null when unreported (0 is a real count)
 * @property {?string} startsAt  ISO 8601 instant with offset, or null
 * @property {?string} watchUrl  external watch link, or null when the wire has no video id
 * @property {?string} platformVideoId  source video id, for the hover-preview embed
 * @property {string[]} tagNames  park/resort tag names, for the Park filter
 * @property {?boolean} surging  Hot flag; null when the wire omits the field
 *   (server kill switch OFF) — null hides all Hot UI, false just hides the badge
 * @property {?number} latestZ  Hot sort key (dashboard number, never shown)
 */

// Resort-level tag name → park code. base/models/broadcast.py add_tags()
// always co-registers the parent (resort) tag alongside any park tag, so
// matching resorts alone is sufficient.
// ponytail: static 3-entry map; extend when a new resort tag ships.
const PARK_CODES = {
  'Walt Disney World': 'WDW',
  'Disneyland Resort': 'DLR',
  'Universal Orlando': 'UOR',
};

/** Blank/missing wire strings become explicit null, never "". */
const orNull = (v) => v || null;

/** @returns {?string} ISO 8601 instant with offset, or null. */
const toInstant = (value) => {
  if (!value) return null;
  const t = new Date(value);
  return isNaN(t.getTime()) ? null : t.toISOString();
};

/**
 * @param {Object} broadcast one element of the /api/broadcasts payload
 * @returns {Stream}
 */
export function normalizeStream(broadcast) {
  const channel = broadcast.channel || {};
  const tags = Array.isArray(broadcast.tags) ? broadcast.tags : [];
  const match = tags.find((t) => PARK_CODES[t && t.name]);
  return {
    id: broadcast._id,
    title: broadcast.title,
    channelName: orNull(channel.title) || orNull(channel.handle),
    channelAvatarUrl: orNull(channel.avatar_url),
    thumbnailUrl: orNull(broadcast.thumbnail_url),
    parkCode: match ? PARK_CODES[match.name] : null,
    tagNames: tags.map((t) => t && t.name).filter(Boolean),
    status: broadcast.is_live ? 'live' : 'upcoming',
    viewerCount: broadcast.active_viewers ?? null,
    startsAt: toInstant(
      broadcast.is_live
        ? broadcast.actual_start_time
        : broadcast.scheduled_start_time
    ),
    // There is no in-app watch view — the legacy Watch button opened
    // YouTube directly, and the card link does the same.
    // ponytail: YouTube-only URL, matching the wire; revisit if a second platform ships.
    watchUrl: broadcast.platform_video_id
      ? `https://www.youtube.com/watch?v=${broadcast.platform_video_id}`
      : null,
    platformVideoId: orNull(broadcast.platform_video_id),
    // Hot (server-gated; no client flag logic): absent field means the kill
    // switch is off — keep that distinct from an explicit false.
    surging: typeof broadcast.surging === 'boolean' ? broadcast.surging : null,
    latestZ: broadcast.latest_z ?? null,
  };
}

/**
 * @param {Object[]} broadcasts the full /api/broadcasts payload
 * @returns {Stream[]}
 */
export function normalizeStreams(broadcasts) {
  return Array.isArray(broadcasts) ? broadcasts.map(normalizeStream) : [];
}
